
import { defineComponent, defineAsyncComponent } from 'vue';
import JsCookie from 'js-cookie';
import FingerPrint from '@/helpers/FingerPrint';
import PusherService from '@/helpers/PusherService';
import setCssVar from 'quasar/src/utils/css-var/set-css-var.js';;
import SetMetaTags from '@/helpers/MetaTagsSetter';
import SetAnalytics from '@/helpers/AnalyticsSetter';

import api from '@/api';
import StartData from '@/models/StartDataModel';
import Message from '@/models/MessageModel';
import NewMessage from '@/models/NewMessageModel';
import Widget from '@/models/WidgetModel';
import Respondent from '@/models/RespondentModel';
import Button from '@/models/ButtonModel';
import Bot from '@/models/BotModel';
import Background from '@/models/BackgroundModel';
import PersistentMenu from '@/components/PersistentMenu/index.vue';
import TypingAnimation from './components/MessageTypes/TypingAnimation.vue';
import AnswerField from '@/components/AnswerField/index.vue';
import BotAvatar from '@/components/App/BotAvatar.vue';
import { PayloadFromServer } from '@/helpers/PusherService/interfaces';
import GetBackground from '@/helpers/GetBackground';
import PostMessageHelper from './helpers/PostMessageHelper';
import Command from '@/models/CommandModel';
import Event from '@/models/EventModel';
import Cookie from '@/models/CookieModel';
import SelectChanelRefactor from '@/components/MessageTypes/SelectChanelRefactor/index.vue';
import i18n from '@/lang/i18n';
import FileUploader from '@/components/MessageTypes/FileUploader.vue';
import Quasar from 'quasar/src/vue-plugin.js';;

interface AvatarPosition {
	top: string;
	left: string;
}

export default defineComponent({
	name: 'App',

	components: {
		FileUploader,
		SelectChanelRefactor,
		PersistentMenu,
		TypingAnimation,
		AnswerField,
		BotAvatar,

		WidgetHeader: defineAsyncComponent(
			() => import('@/components/WidgetHeader/index.vue')
		),

		BackgroundVideo: defineAsyncComponent(
			() => import('@/components/App/BackgroundVideo.vue')
		),

		'text-bot': defineAsyncComponent(
			() => import('@/components/MessageTypes/TextBot.vue')
		),
		choice: defineAsyncComponent(() => import('@/components/MessageTypes/Choice.vue')),
		multiple_choice: defineAsyncComponent(
			() => import('@/components/MessageTypes/MultipleChoice.vue')
		),
		action_typing: defineAsyncComponent(
			() => import('@/components/MessageTypes/TypingAnimation.vue')
		),
		'text-my': defineAsyncComponent(() => import('@/components/MessageTypes/TextMy.vue')),
		carousel: defineAsyncComponent(
			() => import('@/components/MessageTypes/Carousel/index.vue')
		),

		links: defineAsyncComponent(
			() => import('@/components/MessageTypes/Links/index.vue')
		),

		location: defineAsyncComponent(
			() => import('@/components/MessageTypes/Location.vue')
		),
		DynamicBackground: defineAsyncComponent(
			() => import('@/components/DynamicBackground.vue')
		),

		TriggerPreview: defineAsyncComponent(
			() => import('@/components/TriggerPreview/index.vue')
		)
	},

	data() {
		return {
			startData: {} as StartData,
			startSurveyImmediately: false as boolean,
			messages: [] as Array<Message>,
			persistentMenu: [] as Array<Button>,
			widget: {} as Widget,
			bot: {} as Bot,
			fingerPrintService: {} as FingerPrint,
			conversationId: '' as string,
			surveyId: 0 as number,
			respondent: {} as Respondent,
			pusherService: {} as PusherService,
			avatarPosition: { top: '-2000px', left: '15%' } as AvatarPosition,
			windowHeightInPixels: `${window.innerHeight}px` as string,
			isDynamicBackground: false as boolean,
			isWidgetOpen: true as boolean,
			// Нужно нам чтобы если мы открытили виджет, то не показывать превью
			// Пока не придет новое сообщение
			isWidgetWasOpened: false,
			// Метка, открыт ли виджет на мобильнке
			// Узнать это мы можем только с стороннего сайта, где установлен виджет
			isWidgetOpenedAtPhoneScreen: false,
			background: {} as Background,
			dialogMode: false as boolean,
			roistatVisit: undefined as Cookie | undefined,
			ymTrackingId: null as number | null,
			widgetOpeningDesign: 'trigger' as string,
			popupOpened: false as boolean,
			status: ''
		};
	},

	watch: {
		status(value) {
			switch (value) {
				case 'disconnected':
				case 'unavailable':
				case 'failed':
					api.sendRespondentDisconnected(this.bot.project_id, this.respondent.id);
					break;
				default:
					break;
			}
		},
		// Если не получили респондента сразу ждем когда получим и отправляем запрос на сохранение куки в бд
		respondent() {
			this.updateRespondentRoistatVisit();
		},
		// Если сразу не получили куку ждем пока получим и отправляем запрос на сохранение куки в бд
		roistatVisit() {
			this.updateRespondentRoistatVisit();
		},
		// При добавлении сообщения - мы должны держать в самом низу чат
		// В будущем возможно добавится некая логика для этого
		messages: {
			deep: true,

			handler() {
				this.$nextTick(() => {
					this.scrollDown();
				});

				// При новом сообщение сбрасываем то, что виджет был уже открыт
				this.isWidgetWasOpened = false;
			}
		}
	},

	computed: {
		_ym_uid(): string | null {
			const url = window.location.href;
			const name = '_ym_uid';
			const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
				results = regex.exec(url);
			if (!results) return null;
			if (!results[2]) return '';
			return decodeURIComponent(results[2].replace(/\+/g, ' '));
		},

		isShowAnswerField(): boolean {
			if (this.lastMessage?.question_type === 'file') {
				return false;
			}

			if (this.lastMessage?.type === 'action_typing') {
				return false;
			}

			if (this.isHaveLastMessageButtons) {
				return false;
			}

			if (this.dialogMode) {
				return true;
			}

			if (this.botLastMessageWithoutTyping?.type === 'text-bot') {
				return true;
			}

			return false;
		},

		isHaveLastMessageButtons(): boolean {
			const buttons = this.botLastMessageWithoutTyping?.buttons;
			if (buttons) {
				return buttons.length > 0;
			} else {
				return false;
			}
		},

		isShowTriggerPreview(): boolean {
			return (
				this.isWidget &&
				!this.isWidgetOpen &&
				!this.isWidgetWasOpened &&
				this.messages.length > 0 &&
				this.widgetOpeningDesign !== 'none'
			);
		},

		isDialogModeInIframe(): boolean {
			return this.isShowAnswerField && this.isWidget;
		},

		isPhoneSize(): boolean {
			return this.$q.screen.name === 'xs';
		},

		lastMessage(): Message | null {
			if (this.messages.length === 0) {
				return null;
			}

			return this.messages[this.messages.length - 1];
		},

		botLastMessageWithoutTyping(): Message | null {
			if (this.messages.length === 0) {
				return null;
			}
			const messages = this.messages.filter(
				(m) => m.type !== 'action_typing' && m.type !== 'text-my'
			);
			return messages[messages.length - 1];
		},

		lastMessageQuestionType(): string | null {
			return this.lastMessage?.question_type ?? null;
		},

		isShowDynamicalAvatar(): boolean {
			if (
				this.bot.avatar &&
				!this.isPhoneSize &&
				this.lastMessageQuestionType !== 'carousel'
			) {
				return true;
			} else {
				return false;
			}
		},

		// Наследие с старого кода, нужно подумать и протестить без этого метода
		parentBaseUrl(): string {
			const a = window.document.createElement('a');
			a.href = this.widgetParentUrl;
			return a.origin;
		},

		widgetParentUrl(): string {
			return decodeURIComponent(window.location.hash.toString().replace(/^#/, ''));
		},

		queryStringParams(): string {
			const urlWithQueryParams = this.widgetParentUrl
				? this.widgetParentUrl
				: window.location.href;

			const splited = urlWithQueryParams.split('?');
			if (splited.length === 1) {
				return '';
			} else {
				return `?${splited[1]}`;
			}
		},

		isWidget(): boolean {
			return !!this.widgetParentUrl;
		}
	},
	created() {
		if (this.isWidget) {
			this.isWidgetOpen = false;
		}
		window.addEventListener('beforeunload', () => {
			api.sendRespondentDisconnected(this.bot.project_id, this.respondent.id);
		});
	},

	async mounted() {
		// Вешаем вотчер, чтобы корректировать высоту размера экрана
		// Мы могли бы использовать 100vh вместо (но на телефонах это не помогает)
		window.addEventListener('resize', () => {
			this.windowHeightInPixels = `${window.innerHeight}px`;

			// А так же корректируем расположение аватарки
			this.setAvatarPosition(true);
		});

		const url: string = window.location.href;
		const path = url.split('/').reverse()[0];

		const [surveyId, query] = path.split('?');

		const startData = (await api.getStartData(surveyId, query)) as StartData;
		await this.setLangWidget(startData.locale);
		SetMetaTags({
			title: startData.botModel.title
		});

		const { ymTracking } = SetAnalytics(startData.start_data.analytics);
		this.ymTrackingId = ymTracking;

		this.startData = startData;
		this.setDialogMode(this.startData.dialog_mode && this.isWidget);

		// Инициализируем фингер принт
		this.fingerPrintService = FingerPrint.getInstance(
			this.bot.project_id,
			this.startData.fingerprint
		);

		this.surveyId = this.startData.survey_id as number;

		this.startSurveyImmediately = this.startData.start_data.startSurveyImmediately;

		this.persistentMenu = this.startData.start_data.persistent_menu;
		this.widget = this.startData.widget;
		this.bot = this.startData.botModel;

		this.background = GetBackground(this.widget);

		// Устанавливаем в квазар цвета
		setCssVar('primary', this.widget.bg_color);
		this.setFont(this.widget.font);

		const messages = this.startData.start_data.messages as Array<Message>;

		if (messages.length > 0) {
			this.setStartMessages(messages);
		} else {
			await this.getWebbotRespondent();
		}

		if (this.isWidget) {
			this.sendEventsToWidget(this.startData.events);
			this.handleTriggerEventsFromWidget();
		}
	},
	methods: {
		togglePopup() {
			this.popupOpened = !this.popupOpened;
			PostMessageHelper.postMessage({ name: 'togglePopup' });
		},
		closePopup() {
			PostMessageHelper.postMessage({ name: 'close' });
		},
		async setQuasarLocale(locale: 'ru' | 'en' | 'fi') {
			try {
				let langPack;
				switch (locale) {
					case 'fi':
						langPack = await import('quasar/lang/fi');
						break;
					case 'en':
						langPack = await import('quasar/lang/en-US');
						break;
					case 'ru':
						langPack = await import('quasar/lang/ru');
						break;
					default:
						langPack = await import('quasar/lang/ru');
				}
				Quasar.lang.set(langPack.default);
			} catch (error) {
				console.error('Failed to load language pack:', error);
			}
		},
		// Установка языка для виджета
		async setLangWidget(lang: string | null) {
			i18n.global.locale = (lang ?? 'ru') as 'ru' | 'en' | 'fi';

			const locale = i18n.global.locale;
			const data = await api.loadTranslations(locale);

			if (data && data.translations) {
				i18n.global.setLocaleMessage(locale, data.translations);
				await this.setQuasarLocale(locale);
			}
		},
		// Обновляем куку roistat_visit у респондента
		updateRespondentRoistatVisit() {
			// Если получен респондент и есть кука только тогда отправим запрос на сохранение куки в бд
			if (this.respondent.id && this.roistatVisit) {
				api.sendRequiredInputs(this.respondent.id, {
					roistat_visit: this.roistatVisit.value
				});
			}
		},
		isLastMessage(index: number): boolean {
			return this.messages.length - 1 === index;
		},

		// Нужно потому что мы не можем назвать компонент link (так как это зарезервированно html)
		isComponentUse(type: string): string {
			if (type === 'link') {
				return 'links';
			}

			return type;
		},

		isShowBotAvatar(index: number): boolean {
			// Смотрим, если выше по массиву нету сообщений
			// Бота (администратора) то значит нужно показывать аватарку
			for (let i = index + 1; i < this.messages.length; i++) {
				if (this.messages[i].type !== 'text-my') {
					return false;
				}
			}

			return true;
		},

		// Корректирует расположении аватарки
		setAvatarPosition(oneMore: boolean): void {
			// В обертке сообщения устанавливается этот id
			const messageTail = document.getElementById('message-tail');

			if (messageTail) {
				const rect = messageTail.getBoundingClientRect();

				this.avatarPosition = {
					top: `${rect.top - 10}px`,
					left: `${rect.left - 50}px`
				};

				// Нужно нам чтобы докрутить на всякий случай позицию аватарки (На мобильном не исользуется)
				if (oneMore && !this.isPhoneSize) {
					[200, 900].forEach((delay) => {
						setTimeout(() => {
							this.setAvatarPosition(false);
						}, delay);
					});
				}
			}
		},

		// Подставляем полученые сообщения с первого апи запроса
		setStartMessages(startMessages: Array<Message>) {
			let prevMessage: Message | null = null;
			let totalTimeout = 0;
			let delay = 0;

			startMessages.forEach((msg, index) => {
				if (prevMessage && prevMessage.delay) {
					delay = prevMessage.delay * 1000;
					totalTimeout = delay + totalTimeout;
				}

				setTimeout(() => {
					this.messages = this.messages.filter((m) => {
						return m.type !== 'action_typing';
					});

					this.messages.push(msg);

					// Если сообщение не последнее
					// Добавляем тапинг, у нас delay
					if (index < startMessages.length - 1) {
						this.messages.push({ text: '', type: 'action_typing' });
					}

					// Одновременно все ставятся на таймаут и в своем порядке по индексу
					// Вызываются
				}, totalTimeout);

				// Запоминаем предыдущее сообщение в цикле
				prevMessage = Object.assign({}, msg);
			});

			this.startSurveyImmediately = false;
		},

		async getWebbotRespondent(): Promise<void> {
			if (this.respondent.id) {
				return;
			}

			// Получаем респондента и айди чата
			const {
				chat_id: conversationId,
				respondent,
				messages
			} = await api.webbotRespondent(this.bot.project_id, {
				fp: await this.fingerPrintService.getFingerPrint(),
				start_survey: this.startSurveyImmediately,
				is_iframe: this.isWidget,
				survey_id: this.surveyId,
				widget_id: this.widget.id,
				analytic_cookies: {
					facebook_events: JsCookie.get('_fbp'),
					google_analytics: JsCookie.get('_ga'),
					yandex_metrika: this._ym_uid ?? JsCookie.get('_ym_uid')
				},
				ua: window.navigator && window.navigator.userAgent,
				parent_url: this.queryStringParams
			});

			if (this.ymTrackingId) {
				window.ym(this.ymTrackingId, 'userParams', {
					UserID: respondent.id
				});
			}

			if (messages.length) {
				this.setStartMessages(messages);
			}

			this.conversationId = conversationId;
			this.respondent = respondent;

			PostMessageHelper.postMessage({
				name: 'bbWidgetMessage',
				payload: {
					respondent: respondent
				}
			});

			this.initAndSubscribeOnPusher();
		},

		// Инициализируем и подписываемся на пушер
		async initAndSubscribeOnPusher() {
			if (this.pusherService.isSubscribed) {
				return;
			}

			// Функция, которую мы прокидываем в пушер и через которую мы добавляем новое сообщение
			const transferMessages = (newMessage: PayloadFromServer) => {
				console.log('MESSAGE', newMessage);
				switch (this.widgetOpeningDesign) {
					case 'widget':
						PostMessageHelper.postMessage({ name: 'openWidget' });
						break;
					case 'popup':
						PostMessageHelper.postMessage({ name: 'openWidget' });
						this.togglePopup();
						break;
					case 'none':
						if (!newMessage.question_type) {
							this.widgetOpeningDesign = 'auto';
						}
				}
				this.findAndDeleteTypingMessage();

				if (
					newMessage.question_type ||
					(newMessage.buttons && newMessage.buttons?.length > 0)
				) {
					this.setDialogMode(false);
				}

				if (newMessage.type === 'empty') {
					this.setDialogMode(true);
				}

				if (newMessage.action === 'update_cart_button') {
					this.actionUpdateCartButton(newMessage);
				} else {
					this.messages.push(newMessage);
				}
			};

			const transferStatus = (status: string) => {
				this.status = status;
			};

			this.pusherService = PusherService.getInstance(
				this.conversationId,
				transferMessages,
				transferStatus,
				{
					auth: {
						params: {
							fp: await this.fingerPrintService.getFingerPrint(),
							widget_id: this.widget.id,
							survey_id: this.surveyId,
							is_iframe: this.isWidget
						}
					}
				}
			);
		},

		async sendMessage({
			text,
			textBody,
			params,
			type,
			buttonType,
			link,
			attachmentId,
			attachmentError
		}: NewMessage): Promise<void> {
			// Если мы еще не получили чат id, то получаем сначала его
			if (!this.conversationId) {
				await this.getWebbotRespondent();
			}

			if (link) {
				window.open(link, '_blank');
			}

			// if (link && this.respondent.id) {
			// 	OpenLinkWithRespondentId(link, this.respondent.id);
			// }

			if (this.widget.particles_js && this.widget.particles_js === '1') {
				this.isDynamicBackground = true;
			}

			if (text === 'dialog_mode') {
				this.setDialogMode(true);
			}

			const payload = {
				type: type ?? 'text-my',
				text,
				text_body: textBody ?? text,
				params: params ?? {},
				widget_id: this.widget.id,
				survey_id: this.surveyId,
				project_id: this.bot.project_id,
				respondent_id: this.respondent.id
			};
			if (attachmentId) {
				payload['attachment_id'] = attachmentId;
			}

			if (attachmentError) {
				payload['attachment_error'] = attachmentError;
			}

			if (this.botLastMessageWithoutTyping?.question_id) {
				payload['question_id'] = this.botLastMessageWithoutTyping.question_id;
			}

			this.pusherService.sendMessage(payload, buttonType);
		},

		setFont(font: string): void {
			const app = window.document.getElementById('app');
			if (app) {
				app.style.fontFamily = font;
			}
		},

		scrollDown() {
			const wrap = document.getElementsByClassName('messages-wrap')[0];
			const scrollHeight = wrap.scrollHeight;

			wrap.scrollTo(0, scrollHeight);
			this.setAvatarPosition(true);

			this.$nextTick(() => {
				if (scrollHeight !== wrap.scrollHeight) {
					return this.scrollDown();
				}
			});

			setTimeout(() => {
				if (scrollHeight !== wrap.scrollHeight) {
					return this.scrollDown();
				}
			}, 500);
		},

		async sendEventsToWidget(events: Array<Event>): Promise<void> {
			if (events.length > 0) {
				if (!this.conversationId) {
					await this.getWebbotRespondent();
				}
				// Отправляем их в виджет
				PostMessageHelper.postMessage({
					name: 'events',
					payload: {
						events: JSON.parse(JSON.stringify(events))
					}
				});
			}
		},

		findAndDeleteTypingMessage(): void {
			const index = this.messages.findIndex((m) => m.type === 'action_typing');

			if (index !== -1) {
				this.messages.splice(index, 1);
			}
		},
		// Находим нужную карусель и обновляем в ней текст необходимой кнопки
		actionUpdateCartButton(payload: PayloadFromServer): void {
			this.messages.forEach((msg) => {
				msg.slides?.map((slide) => {
					if (payload.product_id === slide.product_id && payload.button) {
						slide.button = payload.button;
					}
				});
			});
		},

		// Обработка событий пришедших с виджета
		handleTriggerEventsFromWidget() {
			// Подписываемся на команды
			PostMessageHelper.subscribeOnMessages(({ data }: { data: Command }) => {
				console.log('DATA>>', data);
				// Здесь внутри в зависимости от названия команды - обрабатываем их
				// Получаем куку roistat_visit
				if (data.name === 'roistatVisit') {
					this.roistatVisit = data.payload?.cookie ?? undefined;
				}
				if (data.name === 'toggleWidget') {
					this.isWidgetOpen = data.payload?.isWidgetOpened ?? false;

					// Ставим метку, что открывали виджет
					this.isWidgetWasOpened = true;
					this.widgetOpeningDesign = 'auto';
					// Устанавливаем, является ли сайт на котором открыли виджет - мобильным
					this.isWidgetOpenedAtPhoneScreen = data.payload?.isPhoneSize ?? false;
					this.$nextTick(() => this.scrollDown());
				}

				// Сработало событие с стороннего сайта
				if (data.name === 'triggerEvent') {
					const triggerId = data.payload?.triggerId;
					const inputValue = data.payload?.triggerInputValue;
					this.widgetOpeningDesign = data.payload?.design || 'auto';
					if (!triggerId) {
						console.error('trigger id not found `data.payload?.triggerId`');
						return;
					}
					this.pusherService.sendTrigger({
						project_id: this.bot.project_id,
						id: triggerId,
						respondent_id: this.respondent.id,
						input_value: inputValue ?? '',
						conversation_id: this.conversationId
					});
				}
			}, this.parentBaseUrl);
		},

		setDialogMode(on: boolean) {
			this.dialogMode = on;
		}
	}
});
